import { useState, useMemo } from 'react';
import { Plus, Loader2, Package, Pencil, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import {
  usePackages,
  usePackage,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
} from '@/hooks/api/usePackages';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  PackageElementType,
  PackageElementPer,
  type PackageWithCount,
} from '@pms/shared';
import type { PackageElementPayload } from '@/services/api/packages.api';

// ─── Constants ─────────────────────────────────────────────────────────────────

const ELEMENT_TYPE_OPTIONS: { value: PackageElementType; label: string }[] = [
  { value: PackageElementType.INCLUSION, label: 'Inclusion' },
  { value: PackageElementType.SERVICE, label: 'Service' },
  { value: PackageElementType.FNB, label: 'F&B' },
];

const ELEMENT_PER_OPTIONS: { value: PackageElementPer; label: string }[] = [
  { value: PackageElementPer.STAY, label: 'Stay' },
  { value: PackageElementPer.NIGHT, label: 'Night' },
  { value: PackageElementPer.PERSON, label: 'Person' },
];

// ─── Local element row (string amount while editing) ───────────────────────────

interface ElementRow {
  name: string;
  elementType: PackageElementType;
  amount: string;
  per: PackageElementPer;
}

function emptyElement(): ElementRow {
  return {
    name: '',
    elementType: PackageElementType.INCLUSION,
    amount: '',
    per: PackageElementPer.STAY,
  };
}

function rowsTotal(rows: ElementRow[]): number {
  return rows.reduce((sum, r) => {
    const n = parseFloat(r.amount);
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
}

function toPayloadElements(rows: ElementRow[]): PackageElementPayload[] {
  return rows.map((r) => {
    const n = parseFloat(r.amount);
    return {
      name: r.name.trim(),
      elementType: r.elementType,
      amount: Number.isFinite(n) && n >= 0 ? n : 0,
      per: r.per,
    };
  });
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function PackagesPage() {
  const propertyId = useSelectedPropertyId();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const listQuery = usePackages({ propertyId }, !!propertyId);
  const allPackages: PackageWithCount[] = listQuery.data ?? [];

  const packages = useMemo(() => {
    if (!search.trim()) return allPackages;
    const q = search.toLowerCase();
    return allPackages.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
    );
  }, [allPackages, search]);

  return (
    <div>
      <PageHeader
        title="Rate Plan Packages"
        description="Bundled inclusions (headers and elements)"
        actions={
          <Button onClick={() => setShowCreate(true)} disabled={!propertyId}>
            <Plus size={16} aria-hidden="true" />
            Add Package
          </Button>
        }
      />

      {!propertyId ? (
        <EmptyState
          icon={Package}
          title="Select a property"
          description="Select a property to continue."
        />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : (
        <>
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or code…"
              className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {packages.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No packages"
              description="Create your first package."
              actionLabel="Add Package"
              onAction={() => setShowCreate(true)}
            />
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <Th>Package Name</Th>
                      <Th>Code</Th>
                      <Th>Elements</Th>
                      <Th>Total Value</Th>
                      <Th>Active</Th>
                      <Th className="text-right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {packages.map((pkg) => (
                      <tr key={pkg.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{pkg.name}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">{pkg.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{pkg.elementsCount}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatMoney(pkg.totalValue)}</td>
                        <td className="px-4 py-3 text-sm">
                          <ActiveBadge active={pkg.isActive} />
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setEditId(pkg.id)}
                            aria-label={`Edit ${pkg.name}`}
                            className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-primary-600 rounded-md hover:bg-gray-100"
                          >
                            <Pencil size={16} aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create slide-over */}
      <SlideOver
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Add Package"
        width="max-w-xl"
      >
        {propertyId && (
          <PackageForm
            mode="create"
            propertyId={propertyId}
            onClose={() => setShowCreate(false)}
          />
        )}
      </SlideOver>

      {/* Edit slide-over */}
      <SlideOver
        isOpen={editId !== null}
        onClose={() => setEditId(null)}
        title="Edit Package"
        width="max-w-xl"
      >
        {editId !== null && (
          <EditPackage id={editId} onClose={() => setEditId(null)} />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Edit loader ───────────────────────────────────────────────────────────────

function EditPackage({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = usePackage(id);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  return (
    <PackageForm
      mode="edit"
      id={id}
      propertyId={data.propertyId}
      initial={{
        name: data.name,
        code: data.code,
        description: data.description ?? '',
        isActive: data.isActive,
        elements: data.elements.map((e) => ({
          name: e.name,
          elementType: e.elementType,
          amount: parseFloat(e.amount).toFixed(2),
          per: e.per,
        })),
      }}
      onClose={onClose}
    />
  );
}

// ─── Form (create + edit) ──────────────────────────────────────────────────────

interface FormInitial {
  name: string;
  code: string;
  description: string;
  isActive: boolean;
  elements: ElementRow[];
}

function PackageForm({
  mode,
  id,
  propertyId,
  initial,
  onClose,
}: {
  mode: 'create' | 'edit';
  id?: string;
  propertyId: string;
  initial?: FormInitial;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [elements, setElements] = useState<ElementRow[]>(
    initial?.elements && initial.elements.length > 0
      ? initial.elements
      : [emptyElement()],
  );

  const createMutation = useCreatePackage();
  const updateMutation = useUpdatePackage();
  const deleteMutation = useDeletePackage();

  const activeMutation = mode === 'create' ? createMutation : updateMutation;

  const elementsValid =
    elements.length > 0 &&
    elements.every((el) => {
      if (el.name.trim().length === 0) return false;
      if (el.amount.trim() !== '') {
        const n = parseFloat(el.amount);
        if (!Number.isFinite(n) || n < 0) return false;
      }
      return true;
    });

  const isValid =
    name.trim().length > 0 && code.trim().length > 0 && elementsValid;

  const total = rowsTotal(elements);

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function updateElement(index: number, patch: Partial<ElementRow>) {
    setElements((prev) =>
      prev.map((el, i) => (i === index ? { ...el, ...patch } : el)),
    );
    clearError();
  }

  function addElement() {
    setElements((prev) => [...prev, emptyElement()]);
  }

  function removeElement(index: number) {
    setElements((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index),
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    const payloadElements = toPayloadElements(elements);

    if (mode === 'create') {
      createMutation.mutate(
        {
          propertyId,
          name: name.trim(),
          code: code.trim(),
          isActive,
          elements: payloadElements,
          ...(description.trim() ? { description: description.trim() } : {}),
        },
        { onSuccess: () => onClose() },
      );
    } else if (id) {
      updateMutation.mutate(
        {
          id,
          payload: {
            name: name.trim(),
            code: code.trim(),
            description: description.trim() ? description.trim() : null,
            isActive,
            elements: payloadElements,
          },
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  function handleDelete() {
    if (!id) return;
    if (!window.confirm('Delete this package and its elements?')) return;
    deleteMutation.mutate(id, { onSuccess: () => onClose() });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Header fields */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="pkg-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="pkg-name"
            type="text"
            required
            value={name}
            onChange={(e) => { setName(e.target.value); clearError(); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="pkg-code" className="block text-sm font-medium text-gray-700 mb-1">
            Code <span className="text-red-500">*</span>
          </label>
          <input
            id="pkg-code"
            type="text"
            required
            maxLength={100}
            value={code}
            onChange={(e) => { setCode(e.target.value); clearError(); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor="pkg-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          id="pkg-desc"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        Active
      </label>

      {/* Elements sub-section */}
      <div className="pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700">Elements</h4>
          <Button type="button" variant="secondary" size="sm" onClick={addElement}>
            <Plus size={14} aria-hidden="true" />
            Add Element
          </Button>
        </div>

        <div className="space-y-3">
          {elements.map((el, i) => {
            const amountInvalid =
              el.amount.trim() !== '' &&
              (!Number.isFinite(parseFloat(el.amount)) || parseFloat(el.amount) < 0);
            const nameInvalid = el.name.trim().length === 0;
            return (
              <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={el.name}
                      onChange={(e) => updateElement(i, { name: e.target.value })}
                      placeholder="Element name *"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {nameInvalid && (
                      <p className="mt-1 text-xs text-red-600">Element name is required.</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeElement(i)}
                    disabled={elements.length === 1}
                    aria-label="Remove element"
                    className="mt-1 p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={el.elementType}
                    onChange={(e) => updateElement(i, { elementType: e.target.value as PackageElementType })}
                    aria-label="Element type"
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {ELEMENT_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={el.amount}
                      onChange={(e) => updateElement(i, { amount: e.target.value })}
                      onBlur={(e) => {
                        const n = parseFloat(e.target.value);
                        if (Number.isFinite(n) && n >= 0) updateElement(i, { amount: n.toFixed(2) });
                      }}
                      placeholder="0.00"
                      aria-label="Amount"
                      className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <select
                    value={el.per}
                    onChange={(e) => updateElement(i, { per: e.target.value as PackageElementPer })}
                    aria-label="Per"
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {ELEMENT_PER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {amountInvalid && (
                  <p className="text-xs text-red-600">Amount must be zero or greater.</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-baseline justify-end gap-2 mt-3">
          <span className="text-sm text-gray-500">Total Value</span>
          <span className="text-base font-semibold text-gray-900">{formatMoney(total)}</span>
        </div>
      </div>

      {activeMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(activeMutation.error)}
        </p>
      )}
      {deleteMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(deleteMutation.error)}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100">
        <div>
          {mode === 'edit' && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleDelete}
              isLoading={deleteMutation.isPending}
            >
              <Trash2 size={16} aria-hidden="true" />
              Delete
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" isLoading={activeMutation.isPending} disabled={!isValid}>
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </div>
      </div>
    </form>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? 'inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700'
          : 'inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600'
      }
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 ${className ?? ''}`}
    >
      {children}
    </th>
  );
}
